/* Page transition loader.
 *
 * Shopify does a full document reload on every navigation, so between the click
 * and the destination's first paint the browser keeps showing the old page
 * unchanged. This shows a full-screen shimmer overlay on that outgoing page.
 *
 * There is deliberately NO hide logic on the destination page: the browser keeps
 * the outgoing document painted until the new one paints, then discards it,
 * overlay and all. First paint of the new document IS the hide, and it is free.
 * The teardown below only covers the cases where the navigation never happens
 * (bfcache restore, cancelled download, history moves).
 *
 * Public API: window.MEPageTransition = { show, hide, navigate, isVisible }
 */
(function () {
  'use strict';

  var OVERLAY_ID = 'me-page-transition';
  var QUOTE_ID = 'me-pt-quote';
  var VISIBLE_CLASS = 'is-visible';

  /* Anchors that do not actually navigate. `aria-controls` is this theme's
     convention for drawer/popup openers (CartDrawer, login-form-popup,
     register-form-popup, drawer-search-form) — those have a real href that JS
     intercepts. Note `data-no-instant` is deliberately NOT here: it means "do not
     prefetch", not "does not navigate", and the theme puts it on the account link. */
  var SKIP_SELECTOR = [
    '[data-no-transition]',
    '[data-action-quickshop]',
    'a[aria-controls]',
  ].join(',');

  var SAFETY_TIMEOUT_MS = 8000;
  var NAV_FALLBACK_MS = 80;

  var overlay = null;
  var safetyTimer = null;
  var visible = false;

  function getOverlay() {
    if (!overlay || !overlay.isConnected) {
      overlay = document.getElementById(OVERLAY_ID);
    }
    return overlay;
  }

  function show() {
    if (visible) return;
    var el = getOverlay();
    if (!el) return;

    /* z-index is already at the 32-bit ceiling, which the sticky cart pill
       (9999999998) and cart drawer also clamp to, so stacking falls back to DOM
       order. Re-append on every show so this wins the tie no matter what has
       been appended to <body> since load (the drawer reparents itself on open).
       Same approach as showImmediateLoadingOverlay() in custom-sticky-cart.js. */
    if (el.parentNode !== document.body || el.nextSibling) {
      document.body.appendChild(el);
    }

    renderQuote();

    visible = true;
    el.classList.add(VISIBLE_CLASS);
    el.setAttribute('aria-hidden', 'false');

    clearTimeout(safetyTimer);
    safetyTimer = setTimeout(hide, SAFETY_TIMEOUT_MS);
  }

  function hide() {
    clearTimeout(safetyTimer);
    safetyTimer = null;
    if (!visible) return;

    var el = getOverlay();
    visible = false;
    if (!el) return;

    el.classList.remove(VISIBLE_CLASS);
    el.setAttribute('aria-hidden', 'true');
  }

  function isVisible() {
    return visible;
  }

  /* Start the navigation only once the overlay has actually been painted.

     Adding the class marks the overlay visible but only *schedules* a paint. If
     the destination responds fast enough (cached collection page), the browser
     can commit the new document before that frame is ever painted, so the loader
     never reaches the screen even though it was switched on correctly. Slower
     destinations get a frame and show it — which is why this looked intermittent
     and link-specific rather than broken.

     Two rAFs: the first callback runs before the frame is painted, the second
     after. The timeout is a hard safety net — a navigation must never be lost
     because a frame never came. One painted frame costs ~16-32ms, which is
     imperceptible next to a page load. */
  function navigateAfterPaint(url) {
    // Nothing to paint into, and rAF is suspended while hidden — go straight there
    // rather than sitting on the fallback timer.
    if (document.visibilityState !== 'visible') {
      window.location.href = url;
      return;
    }

    var done = false;
    function go() {
      if (done) return;
      done = true;
      window.location.href = url;
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(go);
    });
    setTimeout(go, NAV_FALLBACK_MS);
  }

  /* For code that assigns location.href directly (st-back-button, custom.js). */
  function navigate(url) {
    if (!url) return;
    show();
    navigateAfterPaint(url);
  }

  /* ---------- link clicks ---------- */

  function findAnchor(event) {
    /* composedPath so links inside shadow roots (cart drawer, modals) are seen;
       at document level event.target would be retargeted to the host element. */
    var path = typeof event.composedPath === 'function' ? event.composedPath() : null;

    if (path) {
      for (var i = 0; i < path.length; i++) {
        var node = path[i];
        if (node && node.nodeType === 1 && node.tagName === 'A' && node.hasAttribute('href')) {
          return node;
        }
        if (node === document) break;
      }
      return null;
    }

    var target = event.target;
    return target && target.closest ? target.closest('a[href]') : null;
  }

  function shouldSkipAnchor(a) {
    var rawHref = a.getAttribute('href');
    if (!rawHref || rawHref.charAt(0) === '#') return true;

    // New tab / window.
    var target = a.getAttribute('target');
    if (target && target !== '_self') return true;

    if (a.hasAttribute('download')) return true;

    // mailto:, tel:, sms:, whatsapp:, javascript:, intent: ...
    if (a.protocol !== 'http:' && a.protocol !== 'https:') return true;

    if (a.origin !== window.location.origin) return true;

    // Same document, hash-only change: no navigation happens.
    if (a.pathname === window.location.pathname && a.search === window.location.search) {
      return true;
    }

    if (a.closest && a.closest(SKIP_SELECTOR)) return true;

    return false;
  }

  function onClick(event) {
    // Another handler already cancelled the navigation (sold-out popup, drawer
    // openers, returnprime). Listening in the bubble phase is what makes this work.
    if (event.defaultPrevented) return;

    // Middle/right click, or a modified click that opens a new tab.
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var a = findAnchor(event);
    if (!a) return;
    if (shouldSkipAnchor(a)) return;

    // a.href is the resolved absolute URL, so whitespace-padded hrefs (see
    // featured-collection-carousel.liquid) resolve correctly here too.
    var url = a.href;
    if (!url) return;

    event.preventDefault();
    show();
    navigateAfterPaint(url);
  }

  /* ---------- GET form submits (search, collection sort/filter) ---------- */

  function onSubmit(event) {
    if (event.defaultPrevented) return;

    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;

    // POST forms here are cart operations handled over AJAX by HdtCartLoader.
    var method = (form.getAttribute('method') || 'get').toLowerCase();
    if (method !== 'get') return;

    var target = form.getAttribute('target');
    if (target && target !== '_self') return;

    if (form.closest && form.closest(SKIP_SELECTOR)) return;

    try {
      if (new URL(form.action, window.location.href).origin !== window.location.origin) return;
    } catch (e) {
      return;
    }

    show();
  }

  /* ---------- teardown ---------- */

  function onPageShow(event) {
    /* Back/forward restored from bfcache resurrects this frozen document with the
       overlay still up. Without this the user is stuck staring at a loader. */
    if (event.persisted) hide();
  }

  function onVisibilityChange() {
    // A target we misclassified opened elsewhere (new tab, app handoff).
    if (document.visibilityState === 'hidden' && visible) hide();
  }

  /* ---------- quote ---------- */

  var QUOTES = [
    'Our Onion Hair Oil is loved by 10L+ customers across India.',
    'Each order you place contributes to planting a tree 🌳',
    'Mamaearth is Asia’s 1st brand with Made Safe Certified products.',
  ];

  function pickQuote() {
    /* The Liquid renders the list into data-quotes so a quote exists at first
       paint; read it back here so the copy lives in exactly one place. */
    var el = document.getElementById(QUOTE_ID);
    var raw = el && el.getAttribute('data-quotes');
    var list = raw ? raw.split('|').filter(Boolean) : QUOTES;
    if (!list.length) list = QUOTES;
    return list[Math.floor(Math.random() * list.length)];
  }

  // Picks a new quote on every call rather than caching one for the session,
  // so the message actually varies each time the loader is shown.
  function renderQuote() {
    var el = document.getElementById(QUOTE_ID);
    if (el) el.textContent = '“' + pickQuote() + '”';
  }

  /* ---------- init ---------- */

  document.addEventListener('click', onClick);
  document.addEventListener('submit', onSubmit);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('popstate', hide);
  document.addEventListener('visibilitychange', onVisibilityChange);

  window.MEPageTransition = {
    show: show,
    hide: hide,
    navigate: navigate,
    isVisible: isVisible,
  };
})();
